import type { ProPilotMessage, ProPilotMessageStatus } from "PFApp/stores/propilot_store";
import { useProPilotStore, useProPilotStoreActions } from "PFApp/stores/propilot_store";
import { isMsTeams } from "PFCore/helpers/ms_teams";
import type { ConversationId } from "PFCore/services/propilot";
import { IsoDate } from "PFTypes/date_time";
import type { OutgoingFrame } from "PFTypes/propilot";
import { useCallback, useEffect } from "react";

import { useIsProPilotPermitted } from "../use_is_propilot_permitted";
import { useProPilotSocket } from "../use_propilot_socket";
import { clearSessionConversationId } from "../use_session_conversation_id";

type OptionReplyFrame = Extract<OutgoingFrame, { kind: "option_reply" }>;

export type UseProPilotConversationResult = {
  messages: ProPilotMessage[];
  status: ProPilotMessageStatus;
  allowFreeText: boolean;
  isConnected: () => boolean;
  pendingRetry?: string;
  submit: (text: string) => void;
  submitOptionReply: (frame: OptionReplyFrame) => void;
  retry: () => void;
  clearConversation: () => void;
  conversationId: ConversationId;
  destroyConversation: () => void;
};

export const useProPilotConversation = (): UseProPilotConversationResult => {
  const isProPilotEnabled = useIsProPilotPermitted();
  const enabled = isProPilotEnabled && !isMsTeams();

  const { conversationId, submitText, armStreamingTimeout, isConnected, rotateConversationId } =
    useProPilotSocket();

  const {
    appendMessage,
    setStatus,
    setError,
    clearError,
    setPendingRetry,
    createConversation,
    destroyConversation: storeDestroyConversation
  } = useProPilotStoreActions();
  const conversation = useProPilotStore((state) => state.conversations[conversationId]);

  useEffect(() => {
    if (enabled) {
      createConversation(conversationId);
    }
  }, [enabled, conversationId, createConversation]);

  const sendPending = useCallback(
    (text: string) => {
      setPendingRetry(conversationId, { text });
      const delivered = submitText(text);
      if (delivered) {
        setStatus(conversationId, "streaming");
        armStreamingTimeout();
      } else {
        setError(conversationId, "not_open");
      }
    },
    [armStreamingTimeout, conversationId, setError, setPendingRetry, setStatus, submitText]
  );

  const submit = useCallback(
    (text: string) => {
      clearError(conversationId);
      appendMessage(conversationId, {
        id: crypto.randomUUID(),
        author: "user",
        createdAt: new Date().toISOString() as IsoDate,
        content: { type: "generic", text, allowFreeText: true, complete: true }
      });
      sendPending(text);
    },
    [appendMessage, clearError, conversationId, sendPending]
  );

  const submitOptionReply = useCallback(
    (frame: OptionReplyFrame) => {
      submit(frame.text);
    },
    [submit]
  );

  const retry = useCallback(() => {
    const pending = conversation?.pendingRetry;
    if (!pending || conversation?.status !== "error") {
      return;
    }
    clearError(conversationId);
    sendPending(pending);
  }, [clearError, conversation?.pendingRetry, conversation?.status, conversationId, sendPending]);

  const clearConversation = useCallback(() => {
    storeDestroyConversation(conversationId);
    rotateConversationId();
  }, [conversationId, rotateConversationId, storeDestroyConversation]);

  const destroyConversation = useCallback(() => {
    storeDestroyConversation(conversationId);
    clearSessionConversationId();
  }, [conversationId, storeDestroyConversation]);

  const messages = conversation?.messages ?? [];
  const status = conversation?.status ?? "idle";
  const pendingRetry = conversation?.pendingRetry;

  const lastAgentMessage = messages.toReversed().find((msg) => msg.author === "agent");
  const allowFreeText =
    lastAgentMessage?.content.type === "generic" ? (lastAgentMessage.content.allowFreeText ?? true) : true;

  return {
    messages,
    status,
    allowFreeText,
    isConnected,
    pendingRetry,
    submit,
    submitOptionReply,
    retry,
    clearConversation,
    conversationId,
    destroyConversation
  };
};
